/**
 * Resolves a searchable-card click into a `CPT_NEW_DESCRIPTION_SAVED`
 * BigQuery event with `entry_flag: "existing"`. Thin wrapper around
 * {@link logCptNewDescriptionSavedEvent} that handles the
 * option-validation and row → J-code lookup specific to the
 * `cptCodes.tsx#onOptionClick` call site.
 *
 * @module logCptNewDescriptionSelected
 */
import { SearchableCardOption } from "@risalabs_frontend_org/oasis-ui-kit";
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import { GeneralOrderDetails } from "../../../../../data-model/medicalPaOrdersModel";
import { logCptNewDescriptionSavedEvent } from "./logCptNewDescriptionSavedEvent";

/** Arguments accepted by {@link logCptNewDescriptionSelected}. */
interface LogCptNewDescriptionSelectedParams {
  /** Option the user clicked in the searchable-card dropdown. */
  option: SearchableCardOption;
  /**
   * Identifier emitted alongside the option by `SearchableCardCell`. Used to
   * resolve the originating row so the event can include its CPT/J-code.
   */
  rowId: string;
  /** Current CPT rows; used to look up the row by `rowId`. */
  tableData: CptCodesTableRowModel[];
  /** All options shown by the searchable card; used to validate `option.value`. */
  availableOptions: SearchableCardOption[];
  /** General order details (unwrapped — Postgres `.order` payload). */
  generalOrderDetails: GeneralOrderDetails | null | undefined;
}

/**
 * Looks up the row's J-code, validates the clicked option exists in the
 * provided list, and forwards to {@link logCptNewDescriptionSavedEvent}.
 *
 * No-op when the option cannot be matched in `availableOptions` (defensive
 * guard against stale option sets) so the event payload is never logged with
 * an invalid description.
 *
 * @param params - Click context, row lookup data, and order metadata.
 */
export const logCptNewDescriptionSelected = ({
  option,
  rowId,
  tableData,
  availableOptions,
  generalOrderDetails,
}: LogCptNewDescriptionSelectedParams): void => {
  const selectedOption = availableOptions.find((o) => o.value === option.value);
  if (!selectedOption) return;

  const cptCode = tableData.find((row) => row.jCodeId === rowId)?.jCodes?.data;

  logCptNewDescriptionSavedEvent({
    cptDescription: selectedOption.label ?? "",
    cptCode: cptCode ?? "",
    entryFlag: "existing",
    generalOrderDetails,
  });
};
